import { useEffect, useState } from 'react';
import { labToCssRgb, type PaintListItem } from '@pomoshnik/shared';

/**
 * Карточка краски в каталоге (Phase 3 Block 3).
 *
 * Принципиальное отличие от `DecorResultCard`: PaintResultCard **реально
 * рендерит `<img>`** через статически-доступный путь `/paint-photos/<file>`.
 * Decor пока показывает только brand-letter placeholder (фото в R2 через
 * Phase 8). См. ADR-0011 «Paint photos storage strategy».
 *
 * Three-tier visual fallback (от лучшего к худшему):
 *   1. `primaryPhotoKey` задан + image загружается успешно → рендерится `<img>`.
 *   2. Image onError (HTTP 404 / decode fail) или `primaryPhotoKey === null` —
 *      переключение на swatch:
 *        - есть `dominantLab` → `labToCssRgb(dominantLab)` заливает image area
 *          (PAINT_FIXTURE Phase 1 даёт 100% coverage, это типичный defensive path).
 *        - нет `dominantLab` → серый `#cccccc` (data quality marker, крайне редкий).
 *
 * URL encoding: `encodeURI` (а НЕ `encodeURIComponent`) per ADR-0011 §«Decision»:
 * filename содержит уже-валидные path-сегменты с пробелами и кириллицей;
 * `encodeURIComponent` экранировал бы slash, что нужно для будущих nested
 * paths (R2 миграция Phase 8).
 *
 * P-001: globalId передаётся в onItemClick **verbatim**. Encoding для URL
 * делает Container через `encodeURIComponent` в `navigate()`.
 */
export interface PaintResultCardProps {
  item: PaintListItem;
  onItemClick: (globalId: string) => void;
}

const SWATCH_GREY = '#cccccc';

export function PaintResultCard({ item, onItemClick }: PaintResultCardProps) {
  const [imageFailed, setImageFailed] = useState(false);

  // Сброс fail-state при смене photo key (parent reuse React-position для другого item).
  // Без этого sticky imageFailed скрывал бы валидное новое фото — см. code-review C3.
  useEffect(() => {
    setImageFailed(false);
  }, [item.primaryPhotoKey]);

  // Truthy check: schema допускает пустую строку (z.string().nullable() без .min(1)).
  // Без guard'а `<img src="/paint-photos/">` загрузил бы directory listing — см. code-review C3.
  const showImage = !!item.primaryPhotoKey && !imageFailed;
  const swatchBg = item.dominantLab ? labToCssRgb(item.dominantLab) : SWATCH_GREY;
  const swatchAriaLabel = item.dominantLab ? 'Образец цвета краски' : 'нет образца цвета';

  return (
    <button
      type="button"
      onClick={() => onItemClick(item.globalId)}
      aria-label={`Открыть карточку: ${item.name}, ${item.ralCode}`}
      className={[
        'w-full rounded-xl bg-card border-[1.5px] border-accent shadow-card overflow-hidden',
        'text-left p-0 cursor-pointer',
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
      ].join(' ')}
    >
      {/* Image area: aspect-square */}
      <div
        className="w-full aspect-square bg-icon-bg flex items-center justify-center"
        style={!showImage ? { background: swatchBg } : undefined}
        aria-label={!showImage ? swatchAriaLabel : undefined}
        role={!showImage ? 'img' : undefined}
      >
        {showImage ? (
          <img
            src={`/paint-photos/${encodeURI(item.primaryPhotoKey ?? '')}`}
            alt={`Краска ${item.name}, ${item.ralCode}`}
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>

      {/* Content area */}
      <div className="p-5 flex flex-col gap-2">
        <p className="font-body text-[18px] font-semibold text-ink m-0 line-clamp-2">{item.name}</p>

        <p className="text-muted text-base m-0">{item.brand}</p>

        <div className="flex flex-wrap gap-2">
          <Badge>{item.ralCode}</Badge>
          <Badge>{item.category}</Badge>
        </div>
      </div>
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-sm px-3 py-1 rounded-full bg-icon-bg text-separator">{children}</span>
  );
}
